/**
 * IndexedDB Storage Adapter for redux-persist (using Dexie)
 *
 * Replaces the default localStorage backend to avoid the 5MB quota limit.
 * Uses a separate database (ReduxPersistDB) to avoid conflicts with
 * WorkerPortalDB and AdminContextDB.
 *
 * Includes a one-time migration that moves existing localStorage data
 * into IndexedDB on first load after deployment.
 */

import Dexie, { Table } from 'dexie';

interface IKeyValue {
  key: string;
  value: string;
}

const db = new Dexie('ReduxPersistDB');

// Version is permanent — this schema (simple key-value) will never change.
// Do NOT bump this version unless adding new tables/indexes to this database.
db.version(1).stores({ keyval: 'key' });

const table = (db as Dexie & { keyval: Table<IKeyValue, string> }).keyval;

/**
 * One-time migration flag to avoid checking localStorage on every getItem call.
 * Once migration runs (or is confirmed unnecessary), this is set to true.
 */
let migrationComplete = false;

/**
 * Migrates persisted redux state from localStorage to IndexedDB.
 * Runs once on the first getItem call after deploy.
 */
const migrateFromLocalStorage = async (): Promise<void> => {
  if (migrationComplete) return;
  migrationComplete = true;

  try {
    // Find all persist:* keys in localStorage
    const keysToMigrate: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('persist:')) {
        keysToMigrate.push(key);
      }
    }

    if (keysToMigrate.length === 0) return;

    // Migrate each key to IndexedDB
    for (const key of keysToMigrate) {
      const value = localStorage.getItem(key);
      if (value) {
        await table.put({ key, value });
      }
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.error('[idbStorage] Migration from localStorage failed:', error);
  }
};

const idbStorage = {
  getItem: async (key: string): Promise<string | null> => {
    await migrateFromLocalStorage();
    const item = await table.get(key);
    return item?.value ?? null;
  },
  setItem: async (key: string, value: string): Promise<void> => {
    await table.put({ key, value });
  },
  removeItem: async (key: string): Promise<void> => {
    await table.delete(key);
  },
};

export default idbStorage;
